import { useState, useEffect } from 'react'

interface Field {
  name: string
  label: string
  type: 'text' | 'textarea' | 'number' | 'select' | 'json' | 'checkbox' | 'url'
  options?: { value: string; label: string }[]
  placeholder?: string
  required?: boolean
}

interface Props {
  fields: Field[]
  initial: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  onCancel: () => void
  loading?: boolean
}

export default function AdminForm({ fields, initial, onSubmit, onCancel, loading }: Props) {
  const [form, setForm] = useState<Record<string, any>>({})

  useEffect(() => {
    setForm({ ...initial })
  }, [initial])

  const set = (key: string, value: any) => setForm((prev) => ({ ...prev, [key]: value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(form)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {fields.map((field) => (
          <div key={field.name} className={field.type === 'textarea' || field.type === 'json' ? 'md:col-span-2' : ''}>
            <label className="block text-sm font-medium text-gray-300 mb-2">{field.label}</label>

            {field.type === 'textarea' && (
              <textarea
                value={form[field.name] ?? ''}
                onChange={(e) => set(field.name, e.target.value)}
                className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-electric-blue transition-colors min-h-[120px]"
                placeholder={field.placeholder}
                required={field.required}
              />
            )}

            {field.type === 'json' && (
              <textarea
                value={JSON.stringify(form[field.name] ?? [], null, 2)}
                onChange={(e) => {
                  try { set(field.name, JSON.parse(e.target.value)) } catch {}
                }}
                className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-electric-blue transition-colors font-mono text-xs min-h-[100px]"
                placeholder={field.placeholder}
              />
            )}

            {field.type === 'select' && (
              <select
                value={form[field.name] ?? ''}
                onChange={(e) => set(field.name, e.target.value)}
                className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-electric-blue transition-colors"
                required={field.required}
              >
                <option value="">Select...</option>
                {field.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}

            {field.type === 'checkbox' && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form[field.name]}
                  onChange={(e) => set(field.name, e.target.checked)}
                  className="w-4 h-4 rounded border-white/20 bg-gray-800 text-electric-blue focus:ring-electric-blue"
                />
                <span className="text-gray-400 text-sm">{field.placeholder || 'Enabled'}</span>
              </label>
            )}

            {field.type === 'number' && (
              <input
                type="number"
                value={form[field.name] ?? ''}
                onChange={(e) => set(field.name, Number(e.target.value))}
                className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-electric-blue transition-colors"
                placeholder={field.placeholder}
                required={field.required}
              />
            )}

            {(field.type === 'text' || field.type === 'url') && (
              <input
                type={field.type === 'url' ? 'url' : 'text'}
                value={form[field.name] ?? ''}
                onChange={(e) => set(field.name, e.target.value)}
                className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-electric-blue transition-colors"
                placeholder={field.placeholder}
                required={field.required}
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-4 pt-4 border-t border-white/10">
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2.5 bg-electric-blue hover:bg-electric-blue/80 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-gray-300 font-medium rounded-lg transition-colors">
          Cancel
        </button>
      </div>
    </form>
  )
}
